/**
 * Analytics slice (PX-W6.4) — the two project analytics figures behind the
 * utilisation page's live section. Standard `fetchCache` discipline, one
 * cache per figure, keyed by project slug.
 *
 * `velocity` is stored VERBATIM: an `available:false` payload with its reason
 * and empty `points` is the honest answer (until Wave 4's board exists), and
 * the page renders exactly that — the reason, no bars, no invented points.
 */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { isApiError, toApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  shouldFetch,
  type FetchCache
} from '@/store/fetchCache'
import { analyticsServiceHttp } from '../services/analyticsService.http'
import type { GateCycleTimeSummary, VelocityResponse } from '../types'

/** A failed thunk's payload — the i18n-mappable key plus the raw message. */
export interface AnalyticsError {
  /**
   * The SERVER's HTTP status, carried verbatim. Without it a 403 is
   * structurally indistinguishable from a 500 by the time it reaches a page,
   * which is how AC-6.2 shipped unimplemented on two surfaces (merged review
   * C2): they hardcoded a status into `ErrorPanel` and could never branch to
   * `ForbiddenPanel`. `0` means the request never reached the server.
   */
  status: number
  key?: string
  message: string
}

export interface AnalyticsState {
  cycleTime: GateCycleTimeSummary | null
  velocity: VelocityResponse | null
  caches: { cycleTime: FetchCache; velocity: FetchCache }
  error: AnalyticsError | null
}

const initialState: AnalyticsState = {
  cycleTime: null,
  velocity: null,
  caches: {
    cycleTime: { ...initialFetchCache },
    velocity: { ...initialFetchCache }
  },
  error: null
}

const toAnalyticsError = (error: unknown): AnalyticsError => {
  const apiError = isApiError(error) ? error : toApiError(error)
  return { status: apiError.status, key: apiError.backendKey, message: apiError.message }
}

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

/** Load the gate cycle-time summary. */
export const fetchCycleTime = createAsyncThunk<
  GateCycleTimeSummary,
  string,
  { rejectValue: AnalyticsError }
>('analytics/fetchCycleTime', async (projectId, { rejectWithValue }) => {
  try {
    return await analyticsServiceHttp.cycleTime(projectId)
  } catch (error) {
    return rejectWithValue(toAnalyticsError(error))
  }
}, {
  condition: (projectId, { getState }) => {
    const { analytics } = getState() as { analytics: AnalyticsState }
    return shouldFetch(analytics.caches.cycleTime, projectId)
  }
})

/** Load the velocity answer — verbatim, `available:false` included. */
export const fetchVelocity = createAsyncThunk<
  VelocityResponse,
  string,
  { rejectValue: AnalyticsError }
>('analytics/fetchVelocity', async (projectId, { rejectWithValue }) => {
  try {
    return await analyticsServiceHttp.velocity(projectId)
  } catch (error) {
    return rejectWithValue(toAnalyticsError(error))
  }
}, {
  condition: (projectId, { getState }) => {
    const { analytics } = getState() as { analytics: AnalyticsState }
    return shouldFetch(analytics.caches.velocity, projectId)
  }
})

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

export const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
    /** Dismiss a surfaced error and re-arm both caches (the Retry path). */
    analyticsErrorDismissed(state) {
      state.error = null
      invalidate(state.caches.cycleTime)
      invalidate(state.caches.velocity)
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCycleTime.pending, (state, action) => {
        if (state.caches.cycleTime.loadedKey !== action.meta.arg) state.cycleTime = null
        fetchStarted(state.caches.cycleTime)
        state.error = null
      })
      .addCase(fetchCycleTime.fulfilled, (state, action) => {
        state.cycleTime = action.payload
        fetchSucceeded(state.caches.cycleTime, action.meta.arg)
      })
      .addCase(fetchCycleTime.rejected, (state, action) => {
        fetchFailed(state.caches.cycleTime)
        state.error = action.payload ?? null
      })
      .addCase(fetchVelocity.pending, (state, action) => {
        if (state.caches.velocity.loadedKey !== action.meta.arg) state.velocity = null
        fetchStarted(state.caches.velocity)
        state.error = null
      })
      .addCase(fetchVelocity.fulfilled, (state, action) => {
        state.velocity = action.payload
        fetchSucceeded(state.caches.velocity, action.meta.arg)
      })
      .addCase(fetchVelocity.rejected, (state, action) => {
        fetchFailed(state.caches.velocity)
        state.error = action.payload ?? null
      })
  }
})

export const { analyticsErrorDismissed } = analyticsSlice.actions
